import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Preset, AnimationConfig, AnimationCategory, AnimationSubType } from '../types';

type PreviewBackground = 'dark' | 'light' | 'transparent' | 'gradient';

interface AnimationState {
  currentPreset: Preset | null;
  presets: Preset[];
  isPlaying: boolean;
  selectedCanvas: string | null;
  previewBackground: PreviewBackground;
  activeCategoryTab: AnimationCategory;

  setCurrentPreset: (preset: Preset | null) => void;
  updateConfig: (config: Partial<AnimationConfig>) => void;
  setCategory: (category: AnimationCategory) => void;
  setPlaying: (playing: boolean) => void;
  addPreset: (preset: Preset) => void;
  removePreset: (id: string) => void;
  savePreset: () => void;
  setPreviewBackground: (bg: PreviewBackground) => void;
  resetConfig: () => void;
  setActiveCategoryTab: (tab: AnimationCategory) => void;
}

const defaultConfig: AnimationConfig = {
  speed: 1,
  intensity: 0.5,
  color: '#ff0000',
  opacity: 1,
  count: 100,
  size: 3,
  direction: 'down',
  wind: 0,
  gravity: 0,
  waveEnabled: false,
  waveAmplitude: 10,
  waveFrequency: 0.02,
  waveSpeed: 1,
  snowflakeStyle: 'flake',
  depthEnabled: true,
  depthLayers: 3,
  depthFocus: 0.5,
  depthBlur: false,
  cursorEffect: 'none',
  cursorRadius: 100,
  cursorStrength: 1,
  cursorInertia: 0.95,
  scaleCount: true,
  // Button Firefly defaults
  fireflySize: 6,
  fireflyGlow: 15,
  fireflySpeed: 1,
  fireflyBlink: 80,
  fireflyTrail: 1,
  fireflyBlur: 1,
  fireflyColor: '#00ffaa',
  // Border Glow defaults
  borderRadius: 10,
  borderWidth: 0,
};

export const useAnimationStore = create<AnimationState>()(
  persist(
    (set, get) => ({
      currentPreset: null,
      presets: [],
      isPlaying: true,
      selectedCanvas: null,
      previewBackground: 'dark',
      activeCategoryTab: 'backgrounds',

      setCurrentPreset: (preset) => set({ currentPreset: preset }),

      updateConfig: (newConfig) => {
        const { currentPreset } = get();
        if (currentPreset) {
          set({
            currentPreset: {
              ...currentPreset,
              config: { ...currentPreset.config, ...newConfig },
              updatedAt: new Date().toISOString(),
            },
          });
        }
      },

      setCategory: (category) => {
        const { currentPreset } = get();
        if (currentPreset) {
          set({
            currentPreset: {
              ...currentPreset,
              category,
              updatedAt: new Date().toISOString(),
            },
          });
        }
      },

      setPlaying: (playing) => set({ isPlaying: playing }),

      addPreset: (preset) =>
        set((state) => ({
          presets: [...state.presets, preset],
        })),

      removePreset: (id) =>
        set((state) => ({
          presets: state.presets.filter((p) => p.id !== id),
        })),

      savePreset: () => {
        const { currentPreset, presets } = get();
        if (!currentPreset) return;

        const existing = presets.find((p) => p.id === currentPreset.id);
        if (existing) {
          set({
            presets: presets.map((p) => (p.id === currentPreset.id ? currentPreset : p)),
          });
        } else {
          set({ presets: [...presets, currentPreset] });
        }
      },

      setPreviewBackground: (bg) => set({ previewBackground: bg }),

      resetConfig: () => {
        const { currentPreset } = get();
        if (currentPreset) {
          set({
            currentPreset: {
              ...currentPreset,
              config: { ...defaultConfig },
              updatedAt: new Date().toISOString(),
            },
          });
        }
      },

      setActiveCategoryTab: (tab) => set({ activeCategoryTab: tab }),
    }),
    {
      name: 'animation-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        presets: state.presets,
        currentPreset: state.currentPreset,
        previewBackground: state.previewBackground,
        activeCategoryTab: state.activeCategoryTab,
      }),
    }
  )
);

export const createNewPreset = (
  name: string,
  category: AnimationCategory,
  subType: AnimationSubType
): Preset => ({
  id: crypto.randomUUID(),
  name,
  category,
  subType,
  config: { ...defaultConfig },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  isPublic: false,
});
